// 共有出品フォーム(mypage my/items/new・未ログイン出品 items/new = 同一の
// application/items/_common_item_fields 経由)の submit 時に、name/model_number/caption から
// 翻訳プロキシのコピペで混入する HTML タグ・山括弧・不可視文字を除去する。
//
// 多層防御の上乗せであり、権威は BE(app/models/item.rb の strip_pollution / sanitize_text /
// sanitize_multiline)。フォーム経由はここで除去して見た目の汚染を防ぎ、API/JS 回避経路は BE が
// catch-all で浄化する。本モジュールは BE の strip_pollution を「正本」として 1:1 でミラーする。
// 除去ロジックを変える場合は必ず BE 側と同時に更新すること。
//
// 意図的に割愛するもの(BE に委ねる):
//   - 長さ上限(maxlength / 422) … FE は maxlength 属性で別途担保、超過時の 422 は BE
//   - 空になった場合のダミー当て込み … 本番実測で純汚染→空は 0 件。BE のダミー/422 に委ねる
// やらないこと: paste 時・リアルタイム除去(submit 時のみ。貼り付け直後の表示はそのまま)
//
// スコープ: 新規出品フォーム(#form_validated)限定。公開済み編集フォーム(my/items/_form_for_published)は
//   #form_validated を持たないため FE は丸ごと no-op で、そこの model_number / additional_caption(編集追記欄)は
//   BE のみが浄化する(normalize_text_fields / append_caption_with_updated_time → sanitize_multiline)。
//   ＝「編集も FE で浄化済み」ではない(BE が catch-all)。

// ── BE 定数のミラー(app/models/item.rb) ──
// CGI.unescapeHTML 相当: Ruby CGI と同じく apos/amp/quot/gt/lt と数値文字参照のみ・1 パス。
// (textarea/innerHTML による DOM decode は &nbsp; 等まで広く復号して BE と乖離するため使わない)
const ENTITY_RE = /&(apos|amp|quot|gt|lt|#[0-9]+|#[xX][0-9A-Fa-f]+);/g;

// タグ検出の空白は ASCII 空白のみ(\s ではなく [ \t\r\n\f\v])。BE Ruby の \s は ASCII のみだが
// JS の \s は U+3000/NBSP も含むため、そのまま \s を使うとタグ名直後が全角空白のタグ(例 <font[U+3000]color=...>)で
// FE だけ一致して保存値が BE と分岐する。ASCII 固定で BE と厳密に一致させる。
const TAG_WS = '[ \\t\\r\\n\\f\\v]';
// 属性付き開きタグ(<font dir="auto" ...>)。`=` を要求し <DT 990> 等の型番を誤認しない。
const ATTR_OPEN_TAG_RE = new RegExp(`<[a-zA-Z][a-zA-Z0-9]*${TAG_WS}+[^<>]*=[^<>]*>`, 'g');
// 閉じタグ(</font> 等・名前非依存)。
const CLOSE_TAG_RE = new RegExp(`</[a-zA-Z][a-zA-Z0-9]*${TAG_WS}*>`, 'g');
// caption(複数行)では <br> を改行として保持するため、除去ではなく \n へ置換する用に分離。
const BR_TAG_RE = /<\/?br\b[^>]*>/gi;
// bare(属性なし)でも丸ごと除去する安全な実在 HTML タグの allowlist。
const BARE_REMOVE_TAGS = [
  'font', 'div', 'span', 'strong', 'table', 'thead', 'tbody', 'tfoot', 'colgroup',
  'blockquote', 'section', 'article', 'header', 'footer', 'nav', 'aside', 'main', 'figure', 'figcaption',
  'hgroup', 'center', 'marquee', 'style', 'script',
];
const BARE_OPEN_RE = new RegExp(`<(?:${BARE_REMOVE_TAGS.join('|')})${TAG_WS}*>`, 'gi');
// タグ除去後に残った山括弧の塊を半角空白へ置換(<R230>→ R230 ・<<A>>BEGINNING→ A BEGINNING)。
// 除去でなく空白にするのは値の区切りだった括弧の癒着(128GB<MCZ00032>→128GBMCZ00032 等)を防ぐため。
// 塊単位([<>]+)で連続括弧も空白1個にする。lookbehind を使わないので全ブラウザでパース可能。
const ANGLE_RE = /[<>]+/g;
// 削除する記号(空白化でなく削除=詰める)。BE の REMOVE_PUNCT_RE と同一。unescape 後段で実行するため
// エンティティ形(&quot; 等)も復号後に削除される。CSV/コードに効く ASCII 系とアポストロフィ系のみ削除し、
// CSV 無害な全角・スマートダブルは保持する。
//   ダブル " (ASCII のみ)・バッククォート ` / アポストロフィ ' ‘ ’(全て・U+2019 もアポストロフィ役) / カンマ , (半角のみ)
// 残す(削除しない): スマートダブル “ ” ・全角 ＂ ，・読点 、・プライム ′ ″ ・鉤括弧「」『』・; \ | 。
const REMOVE_PUNCT_RE = /["`'‘’,]/g;
// 制御文字(C0/C1)を削除。tab(U+0009)・改行(U+000A/U+000D)は除外。BE の CONTROL_CHARS_RE と同一。
// 制御文字を意図的に対象とするため no-control-regex を無効化する。
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u0080-\u009F]/g;
// ゼロ幅/BOM/方向制御/ソフトハイフン(BE の INVISIBLE_CHARS と同一)。
const INVISIBLE_CHARS_RE = /[\u200B\uFEFF\u200E\u200F\u00AD]/g;
// 連続空白(半角/タブ/改行/NBSP)→半角1個。中間の全角 U+3000 は保持。BE の単一行用 collapse と同一。
const COLLAPSE_SPACE_RE = /[ \t\r\n\u00A0]+/g;
// 前後の空白(全角 U+3000 含む)を trim。\s は U+3000 を含む。
const TRIM_RE = /^\s+|\s+$/g;

// CGI.unescapeHTML(value) のミラー。1 パスで復号し、再スキャンしない(BE と同じく非冪等を許容)。
function unescapeHtmlEntities(value) {
  if (value.indexOf('&') === -1) return value;
  return value.replace(ENTITY_RE, (whole, ref) => {
    switch (ref) {
      case 'apos': return "'";
      case 'amp': return '&';
      case 'quot': return '"';
      case 'gt': return '>';
      case 'lt': return '<';
      default: {
        const num = ref.slice(1); // 先頭の '#' を除く
        const code = (num[0] === 'x' || num[0] === 'X')
          ? parseInt(num.slice(1), 16)
          : parseInt(num, 10);
        try {
          return String.fromCodePoint(code);
        } catch (e) {
          return whole; // 範囲外コードポイントは元のまま(BE も chr 不能なら据え置き)
        }
      }
    }
  });
}

// Item.strip_pollution(text, multiline:) のミラー。
function stripPollution(text, multiline) {
  const t = text
    .replace(BR_TAG_RE, multiline ? '\n' : '') // <br>
    .replace(ATTR_OPEN_TAG_RE, '')             // 属性付き開きタグ(名前非依存)
    .replace(CLOSE_TAG_RE, '')                 // 閉じタグ(名前非依存)
    .replace(BARE_OPEN_RE, '')                 // allowlist の bare 開きタグ(丸ごと)
    .replace(ANGLE_RE, ' ')                    // 残った山括弧の塊を空白化(<R230>→ R230 ・値の癒着防止)
    .replace(REMOVE_PUNCT_RE, '')              // クォート類/カンマを削除(空白化でなく詰める・読点は残す)
    .replace(CONTROL_CHARS_RE, '');            // 制御文字(tab/改行以外の C0/C1)を削除
  // caption の行内タブは半角空白へ(単一行 name/model_number は後段の COLLAPSE で畳まれるため不要)。
  return multiline ? t.replace(/\t/g, ' ') : t;
}

// Item.sanitize_text(value) のミラー(name / model_number)。空白整理 + 前後 trim。
export function sanitizeText(value) {
  if (!value) return value;
  return stripPollution(unescapeHtmlEntities(value), false)
    .replace(INVISIBLE_CHARS_RE, '')
    .replace(COLLAPSE_SPACE_RE, ' ')
    .replace(TRIM_RE, '');
}

// Item.sanitize_multiline(value) のミラー(caption)。改行・行内空白は保持し前後 trim のみ。
export function sanitizeMultiline(value) {
  if (!value) return value;
  return stripPollution(unescapeHtmlEntities(value), true)
    .replace(INVISIBLE_CHARS_RE, '')
    .replace(TRIM_RE, '');
}

// 浄化対象フィールドの単一の定義源(BE の normalize_text_fields に対応)。
// multiline=true は caption(改行保持)、false は name/model_number。
const ITEM_TEXT_FIELDS = [
  { name: 'item[name]', multiline: false },
  { name: 'item[model_number]', multiline: false },
  { name: 'item[caption]', multiline: true },
];

// 1 フィールドを submit 直前に浄化する。
//   - 対象が存在しない/空なら no-op(published 編集の name 非存在等)
//   - 純粋な汚染のみで空になるケースは上書きしない(元値のまま submit し BE のダミー/422 に委ねる)
//   - 変化なしも no-op
function applySanitize(form, fieldName, sanitizer) {
  const field = form.querySelector(`[name="${fieldName}"]`);
  if (!field || !field.value) return;
  const cleaned = sanitizer(field.value);
  if (cleaned === '' || cleaned === field.value) return;
  field.value = cleaned;
}

window.addEventListener('load', () => {
  // #form_validated は form_validation.js も依存する新規出品フォームの標準マーカー。
  // これを起点にフォームへ結合する。浄化対象は ITEM_TEXT_FIELDS の 3 欄(name/model_number/caption)のみで、
  // 各欄の有無は applySanitize 側で個別判定する(欄が一部欠けても残りは処理される。それ以外のカラムには触れない)。
  // #form_validated が無いページ(一覧・詳細・公開済み編集 _form_for_published 等)では何もしない(→ 冒頭スコープ節)。
  const validated = document.getElementById('form_validated');
  const form = validated && validated.closest('form');
  if (!form) return;

  form.addEventListener('submit', () => {
    ITEM_TEXT_FIELDS.forEach(({ name, multiline }) => {
      applySanitize(form, name, multiline ? sanitizeMultiline : sanitizeText);
    });
  });
});
